export class LiveChatState {
	constructor(options = {}) {
		this.currentView = 'home';
		this.isOpen = false;
		this.unreadCount = 0;
		this.activeConversationId = null;

		this.conversations = [];
		this.messages = {};

		this.helpArticles = [];
		this.helpSearchQuery = '';

		this.homeChangelogItems = [];
		this.changelogItems = [];

		this.teamName = options.teamName || 'Support';
		this.teamAvatars = options.teamAvatars || [];
		this.greetingMessage = options.greetingMessage || 'Hi there 👋';
		this.welcomeMessage = options.welcomeMessage || 'How can we help?';
		this.startButtonText = options.startButtonText || 'Send us a message';
		this.showAvatars = options.showAvatars !== false;

		this.metadata = options.metadata || null;
		this.isIdentified = false;
		this.pendingMessage = null;

		// Contact block state from identify / a 403 CONTACT_BLOCKED write.
		this.supportBlocked = false;
		this.feedbackBlocked = false;

		this.enableHelp = options.enableHelp !== false;
		this.enableChangelog = options.enableChangelog !== false;
		this.homeModuleEnabled = options.homeModuleEnabled !== false;
		this.messagesModuleEnabled = options.messagesModuleEnabled !== false;

		this.requireEmailBeforeChat = options.requireEmailBeforeChat || false;
		this.allowAnonymousChat = options.allowAnonymousChat !== false;
		this.allowAttachments = options.allowAttachments !== false;
		this.allowEmoji = options.allowEmoji !== false;
		this.showReplyTime = options.showReplyTime !== false;

		this.agentsOnline = false;
		this.onlineCount = 0;
		this.onlineMessage = options.onlineMessage || "We're online now";
		this.responseTime =
			options.responseTime || 'We typically reply within a few minutes';

		this.businessHoursState = options.businessHoursState || null;
		this.nextOpenAt = options.nextOpenAt || null;
		this.holidayName = options.holidayName || null;
		this.holidayDate = options.holidayDate || null;

		this.typingUsers = {};

		// Real-time connection health, driven by WebSocketService.
		// Values: 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'offline'
		this.connectionState = 'idle';

		// Per-conversation flag: "we sent a message and are waiting for the
		// other side (bot or agent) to respond". Cleared when a typing event
		// or new inbound message arrives, or on send failure.
		this.awaitingReply = {};

		this.isLoading = false;
		this.isLoadingMessages = false;

		this.urls = options.urls || {
			feedback: null,
			changelog: null,
			help: null,
			roadmap: null,
		};

		this._listeners = new Set();
	}

	subscribe(callback) {
		this._listeners.add(callback);
		return () => this._listeners.delete(callback);
	}

	_notify(changeType, data) {
		this._listeners.forEach((cb) => cb(changeType, data, this));
	}

	setView(view) {
		const previousView = this.currentView;
		this.currentView = view;
		this._notify('viewChange', { previousView, currentView: view });
	}

	setOpen(isOpen) {
		this.isOpen = isOpen;
		this._notify('openChange', { isOpen });
	}

	setActiveConversation(conversationId) {
		const previousConversationId = this.activeConversationId;
		this.activeConversationId = conversationId;
		this._notify('conversationChange', {
			conversationId,
			previousConversationId,
		});
	}

	setIdentified(isIdentified, metadata = null) {
		this.isIdentified = isIdentified;
		if (metadata) {
			this.metadata = { ...this.metadata, ...metadata };
		}
		this._notify('identificationChange', { isIdentified, metadata });
	}

	setBlockState({ supportBlocked = false, feedbackBlocked = false } = {}) {
		if (
			this.supportBlocked === supportBlocked &&
			this.feedbackBlocked === feedbackBlocked
		) {
			return;
		}
		this.supportBlocked = supportBlocked;
		this.feedbackBlocked = feedbackBlocked;
		this._notify('blockStateChange', { supportBlocked, feedbackBlocked });
	}

	setConversations(conversations) {
		this.conversations = conversations;
		this._updateUnreadCount();
		this._notify('conversationsUpdate', { conversations });
	}

	addConversation(conversation) {
		this.conversations.unshift(conversation);
		this._updateUnreadCount();
		this._notify('conversationAdded', { conversation });
	}

	setMessages(conversationId, messages) {
		this.messages[conversationId] = messages;
		this._notify('messagesUpdate', { conversationId, messages });
	}

	_getMessageAttachmentsSignature(message) {
		if (
			!Array.isArray(message?.attachments) ||
			message.attachments.length === 0
		) {
			return '';
		}

		return message.attachments
			.map((att) => `${att?.type || ''}:${att?.name || ''}`)
			.join('|');
	}

	_findOptimisticMatchIndex(conversationId, incomingMessage, matchWindowMs) {
		const messages = this.messages[conversationId] || [];
		const incomingTimestamp = Date.parse(incomingMessage.timestamp);
		const incomingSignature =
			this._getMessageAttachmentsSignature(incomingMessage);

		for (let i = messages.length - 1; i >= 0; i--) {
			const candidate = messages[i];
			if (!candidate?.isOptimistic || !candidate?.isOwn) {
				continue;
			}
			if ((candidate.content || '') !== (incomingMessage.content || '')) {
				continue;
			}

			const candidateSignature =
				this._getMessageAttachmentsSignature(candidate);
			if (candidateSignature !== incomingSignature) {
				continue;
			}

			const candidateTimestamp = Date.parse(candidate.timestamp);
			if (
				Number.isNaN(incomingTimestamp) ||
				Number.isNaN(candidateTimestamp) ||
				Math.abs(incomingTimestamp - candidateTimestamp) <= matchWindowMs
			) {
				return i;
			}
		}

		return -1;
	}

	_updateConversationFromMessage(conversationId, message, countUnread) {
		const conv = this.conversations.find((c) => c.id === conversationId);
		if (!conv) {
			return;
		}

		conv.lastMessage = message.content;
		conv.lastMessageTime = message.timestamp;

		if (countUnread && !message.isOwn) {
			conv.unread = (conv.unread || 0) + 1;
			this._updateUnreadCount();
		}
	}

	upsertMessage(conversationId, message, options = {}) {
		if (!this.messages[conversationId]) {
			this.messages[conversationId] = [];
		}

		const reconcileOwnOptimistic = options.reconcileOwnOptimistic === true;
		const optimisticMatchWindowMs = options.optimisticMatchWindowMs || 30000;
		const messages = this.messages[conversationId];
		const existingIndex =
			message?.id != null
				? messages.findIndex((msg) => msg?.id === message.id)
				: -1;

		if (existingIndex !== -1) {
			messages[existingIndex] = {
				...messages[existingIndex],
				...message,
				isOptimistic: false,
			};
			this._updateConversationFromMessage(
				conversationId,
				messages[existingIndex],
				false
			);
			this._notify('messagesUpdate', {
				conversationId,
				messages: [...messages],
			});
			return messages[existingIndex];
		}

		if (reconcileOwnOptimistic && message?.isOwn) {
			const optimisticIndex = this._findOptimisticMatchIndex(
				conversationId,
				message,
				optimisticMatchWindowMs
			);
			if (optimisticIndex !== -1) {
				messages[optimisticIndex] = {
					...messages[optimisticIndex],
					...message,
					isOptimistic: false,
				};
				this._updateConversationFromMessage(
					conversationId,
					messages[optimisticIndex],
					false
				);
				this._notify('messagesUpdate', {
					conversationId,
					messages: [...messages],
				});
				return messages[optimisticIndex];
			}
		}

		const storedMessage = {
			...message,
			isOptimistic: Boolean(message?.isOptimistic),
		};
		messages.push(storedMessage);
		this._updateConversationFromMessage(conversationId, storedMessage, true);
		this._notify('messageAdded', { conversationId, message: storedMessage });
		return storedMessage;
	}

	addMessage(conversationId, message) {
		return this.upsertMessage(conversationId, message);
	}

	updateConversation(conversationId, updates) {
		const conv = this.conversations.find((c) => c.id === conversationId);
		if (!conv) {
			return null;
		}

		Object.assign(conv, updates);
		this._notify('conversationUpdated', { conversationId, conversation: conv });
		return conv;
	}

	markAsRead(conversationId) {
		const conv = this.conversations.find((c) => c.id === conversationId);
		if (conv && conv.unread > 0) {
			conv.unread = 0;
			this._updateUnreadCount();
			this._notify('conversationRead', { conversationId });
		}
	}

	_updateUnreadCount() {
		this.unreadCount = this.conversations.reduce(
			(sum, c) => sum + (c.unread || 0),
			0
		);
		this._notify('unreadCountChange', { count: this.unreadCount });
	}

	setHelpArticles(articles) {
		this.helpArticles = articles;
		this._notify('helpArticlesUpdate', { articles });
	}

	setHelpSearchQuery(query) {
		this.helpSearchQuery = query;
		this._notify('helpSearchChange', { query });
	}

	setHomeChangelogItems(items) {
		this.homeChangelogItems = items;
		this._notify('homeChangelogUpdate', { items });
	}

	setChangelogItems(items) {
		this.changelogItems = items;
		this._notify('changelogUpdate', { items });
	}

	setConnectionState(state) {
		if (this.connectionState === state) return;
		const previousState = this.connectionState;
		this.connectionState = state;
		this._notify('connectionStateChange', { previousState, state });
	}

	setAwaitingReply(conversationId, awaiting) {
		if (!conversationId) return;
		const current = !!this.awaitingReply[conversationId];
		const next = !!awaiting;

		// Always clear any pending auto-hide timer for this conversation
		// on any state change — turning off cancels the timer, turning on
		// resets it (fresh 2 min window per send).
		if (!this._awaitingReplyTimers) this._awaitingReplyTimers = {};
		if (this._awaitingReplyTimers[conversationId]) {
			clearTimeout(this._awaitingReplyTimers[conversationId]);
			delete this._awaitingReplyTimers[conversationId];
		}

		if (current === next) return;
		if (next) {
			this.awaitingReply[conversationId] = true;
			// Auto-hide after 2 min so a truly stalled conversation doesn't
			// leave the three-dot indicator on screen forever.
			this._awaitingReplyTimers[conversationId] = setTimeout(() => {
				delete this._awaitingReplyTimers[conversationId];
				if (this.awaitingReply[conversationId]) {
					delete this.awaitingReply[conversationId];
					this._notify('awaitingReplyChange', {
						conversationId,
						awaiting: false,
					});
				}
			}, 120000);
		} else {
			delete this.awaitingReply[conversationId];
		}
		this._notify('awaitingReplyChange', { conversationId, awaiting: next });
	}

	markMessageFailed(conversationId, messageId) {
		const messages = this.messages[conversationId];
		if (!messages) return;
		const index = messages.findIndex((m) => m && m.id === messageId);
		if (index === -1) return;
		messages[index] = {
			...messages[index],
			isOptimistic: false,
			isFailed: true,
		};
		this._notify('messagesUpdate', {
			conversationId,
			messages: [...messages],
		});
	}

	getActiveConversation() {
		return this.conversations.find((c) => c.id === this.activeConversationId);
	}

	getActiveMessages() {
		return this.messages[this.activeConversationId] || [];
	}

	setTeamAvatarsFromAgents(agents) {
		if (!agents || agents.length === 0) return;

		this.teamAvatars = agents.map((agent) => {
			if (agent.picture) return agent.picture;
			return agent.full_name || '?';
		});
		this._notify('teamAvatarsUpdate', { teamAvatars: this.teamAvatars });
	}

	getFilteredHelpArticles() {
		if (!this.helpSearchQuery) {
			return this.helpArticles;
		}
		const query = this.helpSearchQuery.toLowerCase();
		return this.helpArticles.filter(
			(article) =>
				article.title.toLowerCase().includes(query) ||
				(article.description &&
					article.description.toLowerCase().includes(query))
		);
	}

	reset() {
		const previousView = this.currentView;
		this.currentView = 'home';
		this.activeConversationId = null;
		this.helpSearchQuery = '';
		if (previousView !== 'home') {
			this._notify('viewChange', { previousView, currentView: 'home' });
		}
		this._notify('reset', {});
	}
}
